import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { Study } from '../../../../models/study';
import { AppState } from '../../../../state';
import { selectSelectedLanguage, selectSelectedStudy } from '../../../state/study/study.selectors';
import { StudyType } from '../../../../models/study-type';
import { Intervention } from '../../../../models/intervention';
import { Module } from '../../../../models/module';
import { Metrics } from '../../../../models/metrics';
import { getMetrics, getFilters, getUnansweredQuestions, downloadInterventionParticipantData, resetValues } from '../../../state/metrics/metrics.actions';
import { selectMetrics, selectFilter } from '../../../state/metrics/metrics.selectors';
import { Language } from '../../../../models/language';
import { Participant } from '../../../../models/participant';
import { TiimStudyParticipant } from 'src/app/models/tiim-study-participant';

const METRICS_PAGE_TABS = {
  PREVIEW: 'preview',
  DOWNLOAD: 'download',
  GRAPHS: 'graphs',
};

@Component({
  selector: 'dashboard-metrics',
  templateUrl: './metrics.component.html',
  styleUrls: ['./metrics.component.scss'],
})
export class MetricsComponent implements OnInit, OnDestroy {
  selectedStudy: Study;

  metrics: Metrics;
  modules: Module[];
  participants: Participant[];
  filterInterventions: Intervention[];
  filterModules: Module[];
  filterParticipants: TiimStudyParticipant[];

  repopulateModules = false;
  selectedAllInterventions = false;
  selectedAllParticipants = false;
  selectedAllModules = false;
  showTimings = true;
  displayUnansweredQuestions = false;
  participantIndex = null;
  resetModules = '';
  resetParticipants = '';

  selectedIntervention: Intervention;
  selectedModule: Module;
  selectedParticipant: Participant;
  selectedLanguage: Language;

  tab = METRICS_PAGE_TABS.PREVIEW;

  StudyType = StudyType;
  METRICS_PAGE_TABS = METRICS_PAGE_TABS;

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(selectMetrics).subscribe((metrics) => {
        this.metrics = metrics;
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => {
        this.selectedStudy = selectedStudy;
      })
    );

    this.subscriptions.add(
      this.store.select(selectFilter).subscribe((filter) => {
        const tempInterventions = filter?.filter?.interventions;
        const tempModules = this.metrics?.modules;
        const tempParticipants = filter?.filter?.participants;

        // Create copies, which can actually be edited, filtered etc...
        if (tempInterventions) this.filterInterventions = [...tempInterventions];
        if (tempModules) this.filterModules = [...tempModules];
        if (tempParticipants) this.filterParticipants = [...tempParticipants];

        // Remove modules without a name from the list of options
        // Modules without a name are namely enrolment modules
        this.filterModules?.forEach((element, index) => {
          if (element.name === null) this.filterModules.splice(index, 1);
        });
      })
    );

    this.subscriptions.add(
      combineLatest([this.store.select(selectMetrics), this.store.select(selectSelectedLanguage)]).subscribe(
        ([metrics, selectedLanguage]) => {
          this.selectedLanguage = selectedLanguage;
          this.modules = metrics?.modules;
          if (metrics?.participants) this.filterParticipants = metrics.participants;
        }
      )
    );
  }

  updateMetricsPreview(selectedIntervention: Intervention, selectedModule: Module): void {
    this.repopulateModules = !!selectedIntervention?.id;

    if (selectedIntervention !== null) {
      this.selectedAllInterventions = !!!selectedIntervention?.id;
    } else if (selectedModule !== null) {
      this.selectedAllModules = !!!selectedModule?.id;
    }

    if (selectedIntervention) {
      this.selectedIntervention = selectedIntervention;
      this.selectedModule = null;
      this.selectedParticipant = null;
      this.participantIndex = null;
      this.resetModules = '';
      this.resetParticipants = '';
    }

    if (selectedModule) {
      this.selectedModule = selectedModule;
      this.selectedParticipant = null;
      this.participantIndex = null;
      this.resetParticipants = '';
    }

    if (this.displayUnansweredQuestions) {
      this.store.dispatch(
        getUnansweredQuestions({
          studyId: this.selectedStudy?.id,
          interventionId: this.selectedIntervention?.id,
          moduleId: this.selectedModule?.id,
        })
      );
    } else {
      this.store.dispatch(
        getMetrics({
          studyId: this.selectedStudy?.id,
          interventionId: this.selectedIntervention?.id,
          moduleId: this.selectedModule?.id,
        })
      );
    }
  }

  setShowTimings(showTimings: boolean): void {
    this.showTimings = showTimings;
  }

  setSelectedParticipant(participantId: number): void {
    const allParticipants = this.metrics?.participants;
    this.selectedParticipant = [...allParticipants].find(part => part.id === participantId);
    this.selectedAllParticipants = participantId === -1;

    if (this.selectedAllParticipants) {
      this.participants = allParticipants;
      this.participantIndex = null;
    } else {
      this.selectedParticipant = [...allParticipants].find(part => part.id === participantId);
      this.participants = [];
      this.participants.push(this.selectedParticipant);

      this.participantIndex = 0;

      allParticipants.forEach(part => {
        if (part?.id < participantId) this.participantIndex ++;
      });
    }
  }

  onMetricsTabChange(event): void {
    this.tab = event?.tab?.textLabel?.toString().toLowerCase();
  }

  changeTableView(displayUnansweredQuestions): void {
    this.displayUnansweredQuestions = displayUnansweredQuestions;
    this.store.dispatch(
      getUnansweredQuestions({
        studyId: this.selectedStudy?.id,
        interventionId: this.selectedIntervention?.id,
        moduleId: this.selectedModule?.id,
      })
    );
  }

  downloadData(iid: number, pid: number): void {
    this.store.dispatch(
      downloadInterventionParticipantData({
        interventionId: iid,
        participantId: pid,
      })
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetValues());
    this.subscriptions.unsubscribe();
  }
}
